import React, { useState, useEffect } from 'react';
import { Theme } from '@sumtotal/react-foundation';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as StyledThemeProvider, StyleSheetManager } from 'styled-components';
import rtlPlugin from 'stylis-plugin-rtl';
import PropTypes from 'prop-types';
import getUserTheme from './services/themeService';
import { getLessVariables, evaluateLessVariables } from './services/themeHelper';
import CurrentThemeContext from '../contexts/ThemeContext';
import { getThemeCookieKeyValue } from '../utils/cookieHelper';
import GlobalStyle from './GlobalStyle';
import authService from '../authentication/AuthorizeService';
import getLogger from '../utils/logger';

function ThemeContainer({ children }) {
  const [userTheme, setUserTheme] = useState(Theme());
  const [previewLogoUrl, setPreviewLogoUrl] = useState('');
  const [favIconUrl, setFavIconUrl] = useState('');

  const THEME_COOKIE_KEY = 'k';
  const THEME_COOKIE_USER_KEY = 'u';

  const curThemeKey = getThemeCookieKeyValue(THEME_COOKIE_KEY);
  useEffect(() => {
    (async () => {
      try {
        let currUserIdHash = getThemeCookieKeyValue(THEME_COOKIE_USER_KEY);
        if (currUserIdHash === null || currUserIdHash === undefined) {
          currUserIdHash = 0;
        }
        const userProfile = await authService.getUserProfile();
        if (userProfile && userProfile.personpk) {
          const isRTL = userProfile.isrtl && userProfile.isrtl.toLowerCase() === 'true';
          const isEmulation = await authService.isEmulationMode();
          const { currentTheme } = await getUserTheme(
            !isEmulation && userProfile.useridhash === currUserIdHash ? curThemeKey : null
          );
          if (!currentTheme || !currentTheme.json) {
            setUserTheme(Theme(null, isRTL));
            document.body.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
            return;
          }
          // Extracting less variables object
          const themeVarsObj = getLessVariables(currentTheme);
          // Setting HeaderLogo from Theme
          setPreviewLogoUrl(currentTheme.clientLogo);
          setFavIconUrl(currentTheme.clientFavicon);
          // Evaluating less variables
          const varsJson = await evaluateLessVariables(themeVarsObj);
          const curTheme = Theme(varsJson, isRTL);
          setUserTheme(curTheme);
          document.body.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
        }
      } catch (errorResponse) {
        getLogger().error(errorResponse);
        setUserTheme(Theme());
        document.body.setAttribute('dir', 'ltr');
      }
    })();
  }, [curThemeKey]);

  return (
    <CurrentThemeContext.Provider
      value={{
        themeData: [userTheme, setUserTheme],
        logoData: [previewLogoUrl, setPreviewLogoUrl],
        favIconData: [favIconUrl, setFavIconUrl]
      }}
    >
      <CssBaseline />
      <StyledThemeProvider theme={userTheme}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={userTheme}>
            <GlobalStyle />
            {userTheme.direction === 'rtl' ? (
              <StyleSheetManager stylisPlugins={[rtlPlugin]}>{children}</StyleSheetManager>
            ) : (
              children
            )}
          </ThemeProvider>
        </StyledEngineProvider>
      </StyledThemeProvider>
    </CurrentThemeContext.Provider>
  );
}

// PropType check
ThemeContainer.propTypes = {
  children: PropTypes.node.isRequired
};

export default ThemeContainer;
